// pointsSystem.js
// Points and rewards system utilities for FlowTutor

import { supabase } from './supabaseClient';

// Global flag to track if points system is available
let pointsSystemAvailable = null; // null = not checked, true = available, false = not available

// Points awarded for different activities
export const POINTS_CONFIG = {
  QUIZ_COMPLETION: 10, // Base points for completing a quiz
  PERFECT_SCORE: 50, // Bonus for 100% score
  LESSON_COMPLETION: 20, // Points for completing a lesson (80%+ on quiz)
  STREAK_BONUS_7: 70, // 7-day streak bonus
  STREAK_BONUS_30: 300, // 30-day streak bonus
  SIMULATION_INTERACTION: 5, // Points for interacting with simulations
  FIRST_LESSON: 25, // Bonus for first lesson completion
};

// Check if points system is set up (checks once and caches result)
const checkPointsSystemAvailability = async () => {
  if (pointsSystemAvailable !== null) {
    return pointsSystemAvailable;
  }

  try {
    // Try a simple query to see if the table exists
    const { error } = await supabase
      .from('user_points')
      .select('id')
      .limit(1);

    if (error) {
      // Check for specific errors indicating table doesn't exist
      if (error.code === '42P01' || error.code === '42501' || error.code === 'PGRST116' ||
          error.code === 'PGRST204' || error.status === 406 ||
          error.message?.includes('relation') || error.message?.includes('does not exist') ||
          error.message?.includes('permission denied')) {
        pointsSystemAvailable = false;
        return false;
      }
    }

    pointsSystemAvailable = true;
    return true;
  } catch (error) {
    pointsSystemAvailable = false;
    return false;
  }
};

// Reset the availability check (useful for testing or after setup)
export const resetPointsSystemCheck = () => {
  pointsSystemAvailable = null;
};

// Calculate points based on quiz score
export const calculateQuizPoints = (score) => {
  let points = POINTS_CONFIG.QUIZ_COMPLETION;

  if (score === 100) {
    points += POINTS_CONFIG.PERFECT_SCORE; // Perfect score bonus
  } else if (score >= 90) {
    points += 30; // Excellent performance
  } else if (score >= 80) {
    points += 15; // Good performance
  }

  return points;
};

// Award points to user
export const awardPoints = async (userId, points, activityType, activityData = {}) => {
  // Check if points system is available before making API call
  const isAvailable = await checkPointsSystemAvailability();
  if (!isAvailable) {
    return null; // Silently return without making API call
  }

  try {
    // Call Supabase function to award points
    const { data, error } = await supabase.rpc('award_points', {
      p_user_id: userId,
      p_points: points,
      p_activity_type: activityType,
      p_activity_data: activityData
    });

    if (error) {
      // If we get an error, mark system as unavailable to prevent future calls
      if (error.code === 'PGRST204' || error.code === '42883' || error.status === 406 ||
          error.message?.includes('function') || error.message?.includes('does not exist')) {
        pointsSystemAvailable = false;
        return null;
      }
      return null;
    }

    return data;
  } catch (error) {
    pointsSystemAvailable = false;
    return null;
  }
};

// Get user's current points and level
export const getUserPoints = async (userId) => {
  // Check if points system is available before making API call
  const isAvailable = await checkPointsSystemAvailability();
  if (!isAvailable) {
    return {
      total_points: 0,
      level: 1,
      streak_days: 0
    };
  }

  try {
    const { data, error } = await supabase
      .from('user_points')
      .select('*')
      .eq('user_id', userId)
      .single();

    // Handle errors gracefully - table might not exist yet
    if (error) {
      // PGRST116 = no rows returned
      // PGRST204 = relation (table) does not exist
      // 42P01 = undefined table
      if (error.code === 'PGRST116' || error.code === 'PGRST204' || error.code === '42P01' ||
          error.status === 406 || error.message?.includes('relation') || error.message?.includes('does not exist')) {
        pointsSystemAvailable = false;
        return {
          total_points: 0,
          level: 1,
          streak_days: 0
        };
      }
      return {
        total_points: 0,
        level: 1,
        streak_days: 0
      };
    }

    // If no record exists, return default
    if (!data) {
      return {
        total_points: 0,
        level: 1,
        streak_days: 0
      };
    }

    return data;
  } catch (error) {
    // Silent fail with default values
    return {
      total_points: 0,
      level: 1,
      streak_days: 0
    };
  }
};

// Update user streak
export const updateUserStreak = async (userId) => {
  // Check if points system is available before making API call
  const isAvailable = await checkPointsSystemAvailability();
  if (!isAvailable) {
    return 0;
  }

  try {
    const { data, error } = await supabase.rpc('update_user_streak', {
      p_user_id: userId
    });

    if (error) {
      if (error.code === 'PGRST204' || error.code === '42883' || error.status === 406 ||
          error.message?.includes('function') || error.message?.includes('does not exist')) {
        pointsSystemAvailable = false;
        return 0;
      }
      return 0;
    }

    return data || 0;
  } catch (error) {
    pointsSystemAvailable = false;
    return 0;
  }
};

// Record lesson progress
export const recordLessonProgress = async (userId, goalId, dayNumber, quizScore, pointsEarned) => {
  // Check if points system is available before making API call
  const isAvailable = await checkPointsSystemAvailability();
  if (!isAvailable) {
    return null;
  }

  try {
    const completed = quizScore >= 80; // Auto-complete if 80% or above

    const { data, error } = await supabase
      .from('lesson_progress')
      .upsert({
        user_id: userId,
        goal_id: goalId,
        day_number: dayNumber,
        quiz_score: quizScore,
        points_earned: pointsEarned,
        completed: completed,
        completed_at: completed ? new Date().toISOString() : null,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'user_id,goal_id,day_number'
      })
      .select()
      .single();

    if (error) {
      if (error.status === 406 || error.code === '42P01' || error.code === '42501') {
        pointsSystemAvailable = false;
      }
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error in recordLessonProgress:', error);
    return null;
  }
};

// Check if lesson is completed
export const isLessonCompleted = async (userId, goalId, dayNumber) => {
  // Check if points system is available before making API call
  const isAvailable = await checkPointsSystemAvailability();
  if (!isAvailable) {
    return false;
  }

  try {
    const { data, error } = await supabase
      .from('lesson_progress')
      .select('completed, quiz_score')
      .eq('user_id', userId)
      .eq('goal_id', goalId)
      .eq('day_number', dayNumber)
      .single();

    if (error && error.code !== 'PGRST116') {
      if (error.status === 406 || error.code === '42P01' || error.code === '42501') {
        pointsSystemAvailable = false;
      }
      return false;
    }

    return data?.completed || false;
  } catch (error) {
    pointsSystemAvailable = false;
    return false;
  }
};

// Get all lesson progress for a goal
export const getGoalProgress = async (userId, goalId) => {
  // Check if points system is available before making API call
  const isAvailable = await checkPointsSystemAvailability();
  if (!isAvailable) {
    return [];
  }

  try {
    const { data, error } = await supabase
      .from('lesson_progress')
      .select('*')
      .eq('user_id', userId)
      .eq('goal_id', goalId)
      .order('day_number', { ascending: true });

    if (error) {
      console.error('Error fetching goal progress:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error in getGoalProgress:', error);
    return [];
  }
};

// Get user achievements
export const getUserAchievements = async (userId) => {
  // Check if points system is available before making API call
  const isAvailable = await checkPointsSystemAvailability();
  if (!isAvailable) {
    return [];
  }

  try {
    const { data, error } = await supabase
      .from('user_achievements')
      .select(`
        *,
        achievements (*)
      `)
      .eq('user_id', userId);

    if (error) {
      // Silently handle errors when tables don't exist
      if (error.code === '42P01' || error.code === '42501' || error.code === 'PGRST116' ||
          error.code === 'PGRST204' || error.status === 406 || error.message?.includes('relation') ||
          error.message?.includes('does not exist') || error.message?.includes('permission denied')) {
        pointsSystemAvailable = false;
        return [];
      }
      return [];
    }

    return data || [];
  } catch (error) {
    pointsSystemAvailable = false;
    return [];
  }
};

// Check and award achievements
export const checkAchievements = async (userId) => {
  // Check if points system is available before making API call
  const isAvailable = await checkPointsSystemAvailability();
  if (!isAvailable) {
    return [];
  }

  try {
    // Get user stats
    const userPoints = await getUserPoints(userId);
    const { data: progressData, error: progressError } = await supabase
      .from('lesson_progress')
      .select('*')
      .eq('user_id', userId)
      .eq('completed', true);

    // Silently return if tables don't exist
    if (progressError && (progressError.code === '42P01' || progressError.code === '42501' ||
        progressError.status === 406 || progressError.message?.includes('relation') ||
        progressError.message?.includes('does not exist') || progressError.message?.includes('permission denied'))) {
      pointsSystemAvailable = false;
      return [];
    }

    const lessonsCompleted = progressData?.length || 0;
    const perfectScores = progressData?.filter(p => p.quiz_score === 100).length || 0;

    // Get all achievements
    const { data: allAchievements, error: achievementsError } = await supabase
      .from('achievements')
      .select('*');

    // Silently return if tables don't exist
    if (achievementsError && (achievementsError.code === '42P01' || achievementsError.code === '42501' ||
        achievementsError.message?.includes('relation') || achievementsError.message?.includes('does not exist') ||
        achievementsError.message?.includes('permission denied'))) {
      return [];
    }

    // Get user's current achievements
    const { data: userAchievements, error: userAchError } = await supabase
      .from('user_achievements')
      .select('achievement_key')
      .eq('user_id', userId);

    // Silently return if tables don't exist
    if (userAchError && (userAchError.code === '42P01' || userAchError.code === '42501' ||
        userAchError.message?.includes('relation') || userAchError.message?.includes('does not exist') ||
        userAchError.message?.includes('permission denied'))) {
      return [];
    }

    const earnedKeys = new Set(userAchievements?.map(a => a.achievement_key) || []);
    const newAchievements = [];

    // Check each achievement
    for (const achievement of allAchievements || []) {
      if (earnedKeys.has(achievement.achievement_key)) continue;

      let earned = false;

      switch (achievement.requirement_type) {
        case 'total_points':
          earned = userPoints.total_points >= achievement.requirement_value;
          break;
        case 'streak_days':
          earned = userPoints.streak_days >= achievement.requirement_value;
          break;
        case 'lessons_completed':
          earned = lessonsCompleted >= achievement.requirement_value;
          break;
        case 'perfect_scores':
          earned = perfectScores >= achievement.requirement_value;
          break;
      }

      if (earned) {
        // Award achievement
        const { error } = await supabase
          .from('user_achievements')
          .insert({
            user_id: userId,
            achievement_key: achievement.achievement_key
          });

        if (!error) {
          // Award bonus points
          await awardPoints(userId, achievement.points_reward, 'achievement', {
            achievement_key: achievement.achievement_key,
            achievement_name: achievement.name
          });

          newAchievements.push(achievement);
        }
      }
    }

    return newAchievements;
  } catch (error) {
    // Silently fail if points system not set up
    return [];
  }
};

// Calculate level from points
export const calculateLevel = (points) => {
  return Math.floor(Math.sqrt(points / 100)) + 1;
};

// Calculate points needed for next level
export const pointsForNextLevel = (currentLevel) => {
  const nextLevel = currentLevel + 1;
  return Math.pow(nextLevel - 1, 2) * 100;
};
